import React, { useCallback, useState } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  useReactFlow,
  type EdgeProps,
} from "reactflow";

import "./editableEdge.css";

type EdgeMeta = {
  relationId: number;
  sourceId: number;
  targetId: number;
  multiIndex?: number;
  multiCount?: number;
};

const buildCurvedPath = (
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  multiIndex: number,
  multiCount: number
) => {
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const length = Math.hypot(dx, dy) || 1;
  const nx = -dy / length;
  const ny = dx / length;

  const baseCurve = Math.min(140, Math.max(50, length / 3.5));
  const spread = baseCurve * 0.65;
  const offsetFactor =
    multiCount > 1 ? multiIndex - (multiCount - 1) / 2 : 0;
  const orientation = offsetFactor === 0 ? 1 : Math.sign(offsetFactor);
  const magnitude = baseCurve * 0.35 + Math.abs(offsetFactor) * spread;
  const shiftX = nx * orientation * magnitude;
  const shiftY = ny * orientation * magnitude;

  const cp1 = {
    x: sourceX + dx / 3 + shiftX,
    y: sourceY + dy / 3 + shiftY,
  };
  const cp2 = {
    x: sourceX + (2 * dx) / 3 + shiftX,
    y: sourceY + (2 * dy) / 3 + shiftY,
  };

  const path = `M ${sourceX},${sourceY} C ${cp1.x},${cp1.y} ${cp2.x},${cp2.y} ${targetX},${targetY}`;

  const labelT = Math.min(
    0.8,
    Math.max(0.2, 0.5 + offsetFactor * 0.2)
  );
  const labelX =
    Math.pow(1 - labelT, 3) * sourceX +
    3 * Math.pow(1 - labelT, 2) * labelT * cp1.x +
    3 * (1 - labelT) * Math.pow(labelT, 2) * cp2.x +
    Math.pow(labelT, 3) * targetX;
  const labelY =
    Math.pow(1 - labelT, 3) * sourceY +
    3 * Math.pow(1 - labelT, 2) * labelT * cp1.y +
    3 * (1 - labelT) * Math.pow(labelT, 2) * cp2.y +
    Math.pow(labelT, 3) * targetY;

  return { path, labelX, labelY };
};

const EditableEdge: React.FC<EdgeProps> = (props) => {
  const { id, sourceX, sourceY, targetX, targetY, markerEnd, style, data } =
    props;

  const meta = (data?.meta as EdgeMeta | undefined) ?? undefined;
  const multiIndex = meta?.multiIndex ?? 0;
  const multiCount = meta?.multiCount ?? 1;
  const { path: edgePath, labelX, labelY } = buildCurvedPath(
    sourceX,
    sourceY,
    targetX,
    targetY,
    multiIndex,
    multiCount
  );
  const { setEdges } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState("");

  const labelValue = data?.label ?? "";

  const commitLabelChange = useCallback(
    (nextValue: string) => {
      const trimmed = nextValue.trim();
      const meta = data?.meta as EdgeMeta | undefined;
      const handler = data?.onLabelChange as
        | ((
            relationId: number,
            text: string,
            context: { sourceId: number; targetId: number }
          ) => void)
        | undefined;

      setEdges((edges) =>
        edges.map((edge) =>
          edge.id === id
            ? {
                ...edge,
                data: {
                  ...edge.data,
                  label: trimmed,
                },
              }
            : edge
        )
      );
      if (
        handler &&
        meta &&
        Number.isFinite(meta.relationId) &&
        Number.isFinite(meta.sourceId) &&
        Number.isFinite(meta.targetId)
      ) {
        handler(meta.relationId, trimmed, {
          sourceId: meta.sourceId,
          targetId: meta.targetId,
        });
      }
    },
    [data, id, setEdges]
  );

  const handleStartEditing = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      setDraftValue(labelValue);
      setIsEditing(true);
    },
    [labelValue]
  );

  const handleStopEditing = useCallback(
    (shouldCommit: boolean) => {
      if (shouldCommit) {
        commitLabelChange(draftValue.trim());
      } else {
        setDraftValue(labelValue);
      }
      setIsEditing(false);
    },
    [commitLabelChange, draftValue, labelValue]
  );

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          className="editable-edge-label"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` ,
          }}
          onClick={handleStartEditing}
        >
          {isEditing ? (
            <input
              className="editable-edge-input"
              value={draftValue}
              onChange={(event) => setDraftValue(event.target.value)}
              onBlur={() => handleStopEditing(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleStopEditing(true);
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  handleStopEditing(false);
                }
              }}
              autoFocus
            />
          ) : (
            <span
              className={`editable-edge-text ${
                labelValue?.trim() ? "" : "editable-edge-placeholder"
              }`}
            >
              {labelValue?.trim() ? labelValue : "Add note"}
            </span>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default EditableEdge;
